import { Checkbox, Form, FormField, FormInput, Message, Radio } from 'semantic-ui-react';

import { isMinor } from '../../../utils/registration';

/**
 * Parent / guardian details, for a person who is a minor.
 *
 * **Greyed rather than hidden.** The brief asked for a section that "ungreys" when the person
 * is a child, and that is the better behaviour here anyway: a Centre Manager transcribing a
 * paper form can see the questions that *would* be asked and knows nothing is missing, rather
 * than watching a block of fields appear and wondering what else is conditional.
 *
 * Whether it applies is derived from the date of birth already captured above — there is no
 * second "is this a child?" question to disagree with it.
 */

export interface GuardianState {
    guardianName: string;
    /** `yyyy-MM-dd`, straight from an `<input type="date">` — never a Date instant. */
    guardianDob: string;
    guardianRelationship: string;
    guardianIsMinorNoAlternative: boolean;
    guardianConsentsToChildData: boolean;
    /**
     * The paper form's OR: the guardian's ID was sighted and confirms the relationship, **or**
     * there was no ID and the CM is satisfied anyway. Kept as two booleans rather than an enum
     * so "neither answered yet" stays expressible — which is the state most people start in.
     */
    guardianIdSightedConfirmsRelationship: boolean;
    guardianNoIdButConfirmed: boolean;
}

export const EMPTY_GUARDIAN: GuardianState = {
    guardianName: '',
    guardianDob: '',
    guardianRelationship: '',
    guardianIsMinorNoAlternative: false,
    guardianConsentsToChildData: false,
    guardianIdSightedConfirmsRelationship: false,
    guardianNoIdButConfirmed: false,
};

const DOB_MIN = '1900-01-01';

const localToday = (): string => {
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

export const GuardianSection = ({
    value,
    onChange,
    /** The *person's* date of birth. Undefined or an adult's leaves this section greyed. */
    personDob,
}: {
    value: GuardianState;
    onChange: (next: GuardianState) => void;
    personDob: Date | null;
}) => {
    const applies = isMinor(personDob);
    const guardianIsMinor = isMinor(value.guardianDob);

    const set = (patch: Partial<GuardianState>) => onChange({ ...value, ...patch });

    // The two ID answers are exclusive: choosing one clears the other, so a row can never say
    // both "we saw their ID" and "they had none".
    const chooseIdSighted = () => set({
        guardianIdSightedConfirmsRelationship: true,
        guardianNoIdButConfirmed: false,
    });

    const chooseNoId = () => set({
        guardianIdSightedConfirmsRelationship: false,
        guardianNoIdButConfirmed: true,
    });

    return (
        <div className={`creator-section ${applies ? '' : 'creator-section-disabled'}`}>
            <div className="creator-section-title">Parent or guardian</div>
            <p className="creator-section-hint">
                {applies
                    ? 'This person is under 18, so a parent or guardian gives consent on their behalf.'
                    : 'Only needed for a person under 18. Enter a date of birth above to fill this in.'}
            </p>

            <Form.Group widths="equal">
                <FormInput
                    fluid
                    disabled={!applies}
                    label="Parent / guardian name"
                    placeholder="Full name"
                    value={value.guardianName}
                    onChange={(e) => set({ guardianName: e.target.value })}
                />
                <FormField disabled={!applies}>
                    {/* Explicit id/htmlFor: a bare <label> next to an <input> is not an
                        association, so without this the field has no accessible name. */}
                    <label htmlFor="guardian-dob">Parent / guardian date of birth</label>
                    <input
                        id="guardian-dob"
                        type="date"
                        disabled={!applies}
                        min={DOB_MIN}
                        max={localToday()}
                        value={value.guardianDob}
                        onChange={(e) => set({ guardianDob: e.target.value })}
                    />
                </FormField>
            </Form.Group>

            <FormInput
                fluid
                disabled={!applies}
                label="Relationship to the child"
                placeholder="Mother, father, grandmother, legal guardian…"
                value={value.guardianRelationship}
                onChange={(e) => set({ guardianRelationship: e.target.value })}
            />

            {applies && guardianIsMinor && (
                <Message warning visible>
                    <Message.Header>This parent is under 18</Message.Header>
                    <p>
                        Ask whether there is a parent or guardian over 18 who can give consent. If
                        there is, record them here instead.
                    </p>
                </Message>
            )}

            <FormField disabled={!applies}>
                <div className="consent-toggles">
                    {/* Only offered once the answer to the question above is known to be "no" —
                        showing it unconditionally invites it being ticked instead of asked. */}
                    {applies && guardianIsMinor && (
                        <Checkbox
                            id="guardian-is-minor-no-alternative"
                            label="Parent is a minor, and no alternative adult guardian is available to give consent"
                            checked={value.guardianIsMinorNoAlternative}
                            onChange={() => set({
                                guardianIsMinorNoAlternative: !value.guardianIsMinorNoAlternative,
                            })}
                        />
                    )}
                    <Checkbox
                        id="guardian-consents-to-child-data"
                        disabled={!applies}
                        label="Parent consents to the collection and use of the child's data"
                        checked={value.guardianConsentsToChildData}
                        onChange={() => set({
                            guardianConsentsToChildData: !value.guardianConsentsToChildData,
                        })}
                    />
                </div>
            </FormField>

            <FormField disabled={!applies}>
                <label>Parent's ID</label>
                <div className="consent-toggles">
                    <Radio
                        id="guardian-id-sighted"
                        disabled={!applies}
                        name="guardianIdGroup"
                        label="ID sighted by the Centre Manager, and it confirms the relationship to the child"
                        checked={value.guardianIdSightedConfirmsRelationship}
                        onChange={chooseIdSighted}
                    />
                    <Radio
                        id="guardian-no-id"
                        disabled={!applies}
                        name="guardianIdGroup"
                        label="No ID, but has confirmed to the Centre Manager's satisfaction that they are the named child's parent or guardian, and therefore have the right to give consent"
                        checked={value.guardianNoIdButConfirmed}
                        onChange={chooseNoId}
                    />
                </div>
                <div className="consent-hint">
                    We do not keep a copy of the parent's ID. If the parent is themselves a Joyya
                    participant, their documents live on their own profile.
                </div>
            </FormField>
        </div>
    );
};
